import React, { useEffect, useState } from 'react';
import { X, Music, Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MusicSelector — host-only modal to pick a background-music track.
 * Calls POST /api/live/music/{stream_id}/play to broadcast the choice
 * to all viewers via WebSocket (handled in useLiveStream).
 */
export default function MusicSelector({ open, onClose, streamId, token, current, onPlay, onStop }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/live/music/library`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      setTracks(r.data.tracks || []);
    }).catch(() => toast.error('Could not load music')).finally(() => setLoading(false));
  }, [open, token]);

  if (!open) return null;

  const filtered = tracks.filter((t) =>
    !q || `${t.title} ${t.artist} ${t.genre}`.toLowerCase().includes(q.toLowerCase())
  );

  const handlePlay = async (track) => {
    setBusyId(track.id);
    try {
      const r = await axios.post(
        `${API}/live/music/${streamId}/play`,
        { track_id: track.id, volume: 0.35 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onPlay?.(r.data);
      toast.success(`Now playing: ${track.title}`);
    } catch (e) {
      toast.error('Could not start music');
    }
    setBusyId(null);
  };

  const handleStop = async () => {
    setBusyId('stop');
    try {
      await axios.post(
        `${API}/live/music/${streamId}/stop`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onStop?.();
      toast.success('Music stopped');
    } catch {
      toast.error('Could not stop music');
    }
    setBusyId(null);
  };

  return (
    <div
      data-testid="music-selector-modal"
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-neutral-900 text-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl
                   shadow-2xl ring-1 ring-white/10 max-h-[80vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Music className="w-5 h-5 text-pink-400" />
            <h3 className="font-bold text-lg">Background Music</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10" data-testid="close-music-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {current?.track_id && (
          <div className="mx-4 mt-3 p-3 bg-pink-500/20 ring-1 ring-pink-500/40 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Music className="w-4 h-4 text-pink-300 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{current.title}</p>
                <p className="text-xs text-white/60 truncate">Now playing</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleStop}
              disabled={busyId === 'stop'}
              data-testid="stop-music-btn"
            >
              {busyId === 'stop' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Stop'}
            </Button>
          </div>
        )}

        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tracks..."
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="music-search-input"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 pt-2 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-white/50" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center py-8 text-white/50 text-sm">No tracks found</p>
          ) : (
            filtered.map((track) => {
              const isPlaying = current?.track_id === track.id;
              return (
                <button
                  key={track.id}
                  onClick={() => handlePlay(track)}
                  disabled={busyId === track.id || isPlaying}
                  data-testid={`music-track-${track.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left
                              ${isPlaying ? 'bg-pink-500/30 ring-1 ring-pink-500' : 'bg-white/5 hover:bg-white/10'}
                              disabled:opacity-60`}
                >
                  <img
                    src={track.cover}
                    alt={track.title}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{track.title}</p>
                    <p className="text-xs text-white/50 truncate">{track.genre} · {track.artist}</p>
                  </div>
                  {busyId === track.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-white/60" />
                  ) : isPlaying ? (
                    <span className="text-xs font-bold text-pink-300">PLAYING</span>
                  ) : (
                    <Music className="w-4 h-4 text-white/40" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
